/**
 * Product Service
 *
 * Handles product management within organizations.
 */
import { api } from '../core/api';
import {
  Product,
  ProductListResponse,
  CreateProductRequest,
  UpdateProductRequest,
  ProductDeletePreview,
} from '../types/product';

export const productService = {
  /**
   * Get all products for the current organization
   * @param includeInactive Whether to include inactive products
   */
  async getProducts(includeInactive: boolean = false): Promise<ProductListResponse> {
    return api.get<ProductListResponse>('/products', { include_inactive: includeInactive });
  },

  /**
   * Get all products for a specific organization (overrides org header)
   */
  async getProductsForOrg(organizationId: string): Promise<ProductListResponse> {
    return api.get<ProductListResponse>('/products', undefined, {
      headers: { 'X-Organization-ID': organizationId }
    });
  },

  /**
   * Get a specific product by ID
   */
  async getProduct(id: string): Promise<Product> {
    return api.get<Product>(`/products/${id}`);
  },

  /**
   * Create a new product (admin/owner only)
   */
  async createProduct(data: CreateProductRequest): Promise<Product> {
    return api.post<Product>('/products', data);
  },

  /**
   * Update a product (admin/owner only)
   */
  async updateProduct(id: string, data: UpdateProductRequest): Promise<Product> {
    return api.put<Product>(`/products/${id}`, data);
  },

  /**
   * Get preview of items that will be deleted with the product
   */
  async getDeletePreview(id: string): Promise<ProductDeletePreview> {
    return api.get<ProductDeletePreview>(`/products/${id}/delete-preview`);
  },

  /**
   * Delete a product permanently (owner only)
   * @param id Product ID
   * @param confirmName Product name for confirmation
   */
  async deleteProduct(id: string, confirmName: string): Promise<void> {
    return api.delete<void>(`/products/${id}?confirm_name=${encodeURIComponent(confirmName)}`);
  },
};
